/*
  Newsletter popup.

  The form is a native {% form 'customer' %}: the subscriber is created as a
  Customer with marketing consent and the source tag, and the Shopify
  Marketing Automation sends the discount code by that tag.

  Submit is NOT intercepted. Shopify attaches its own hCaptcha to customer /
  contact forms (ce_storefront_forms_captcha_hcaptcha); it listens to submit,
  runs the captcha and appends a token. Sending the form with our own fetch
  bypassed the captcha — the request arrived without a token, Shopify silently
  rejected it and no customer was created. So we only mark that the submit came
  from the popup and let the browser do the rest.

  After submit the page reloads. Success is detected on load: the pending flag
  in sessionStorage says it was OUR form (without it a footer sign-up would also
  open the popup), and the fact of success is the form.posted_successfully?
  marker from Liquid or ?customer_posted=true in the URL.

  Show memory:
    localStorage.subscribed   — subscribed, never show again
    localStorage.dismissedAt  — closed, don't show for reappearDays days
    localStorage.shownCount   — times shown; maxShows caps the nagging
    sessionStorage seen       — already seen this session

  The session flag is set when the popup is SHOWN, not when it's closed.
  Otherwise someone who didn't close it but just navigated to another page
  would get it again on every step.

  In the theme editor the memory is ignored, otherwise the section can't be
  designed.
*/

(function () {
  const STORAGE_KEY = 'sz:newsletter-popup';
  const SESSION_KEY = 'sz:newsletter-popup:seen';
  const PENDING_KEY = 'sz:newsletter-popup:pending';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';

  function readState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function writeState(patch) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.assign(readState(), patch)));
    } catch (e) {
      /* private mode — live without memory */
    }
  }

  function seenThisSession() {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function markSeenThisSession() {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) {
      /* private mode */
    }
  }

  function setPending() {
    try {
      sessionStorage.setItem(PENDING_KEY, '1');
    } catch (e) {
      /* private mode */
    }
  }

  function takePending() {
    try {
      const v = sessionStorage.getItem(PENDING_KEY) === '1';
      sessionStorage.removeItem(PENDING_KEY);
      return v;
    } catch (e) {
      return false;
    }
  }

  class NewsletterPopup {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('.bznp__panel');
      this.scrim = root.querySelector('.bznp__scrim');
      this.form = root.querySelector('.bznp__form');
      this.error = root.querySelector('.bznp__error');
      this.stateInitial = root.querySelector('[data-state="initial"]');
      this.stateSuccess = root.querySelector('[data-state="success"]');

      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.delay = parseInt(root.dataset.delay, 10) || 0;
      this.scrollPercent = parseInt(root.dataset.scroll, 10) || 0;
      this.exitIntent = root.dataset.exitIntent === 'true';
      this.reappearDays = parseInt(root.dataset.reappearDays, 10) || 0;
      this.maxShows = parseInt(root.dataset.maxShows, 10) || 0;

      this.isOpen = false;
      this.lastFocus = null;
      this.timers = [];
      this.onKeydown = this.onKeydown.bind(this);

      this.bind();
      this.schedule();
    }

    /* ---------- are we allowed to show at all ---------- */

    get suppressed() {
      if (this.designMode) return false;

      const state = readState();
      if (state.subscribed) return true;

      // Shown this session — stay quiet on other pages
      if (seenThisSession()) return true;

      // Shown maxShows times with no response — enough
      if (this.maxShows && (state.shownCount || 0) >= this.maxShows) return true;

      if (!state.dismissedAt) return false;
      if (!this.reappearDays) return true; // 0 days = never show again after closing
      const passed = (Date.now() - state.dismissedAt) / 86400000;
      return passed < this.reappearDays;
    }

    schedule() {
      if (this.designMode) {
        this.open();
        return;
      }
      if (this.handleReturn()) return;
      if (this.suppressed) return;

      if (this.delay > 0) {
        this.timers.push(window.setTimeout(() => this.open(), this.delay * 1000));
      }

      if (this.scrollPercent > 0) {
        this.onScroll = () => {
          const doc = document.documentElement;
          const scrollable = doc.scrollHeight - window.innerHeight;
          if (scrollable <= 0) return;
          if ((window.scrollY / scrollable) * 100 >= this.scrollPercent) this.open();
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      // Exit intent only makes sense with a mouse
      if (this.exitIntent && window.matchMedia('(pointer: fine)').matches) {
        this.onMouseOut = (event) => {
          if (event.clientY <= 0 && !event.relatedTarget) this.open();
        };
        document.addEventListener('mouseout', this.onMouseOut);
      }

      // No trigger configured — show immediately
      if (!this.delay && !this.scrollPercent && !this.exitIntent) this.open();
    }

    /* Page loaded right after a submit from the popup */
    handleReturn() {
      if (!takePending()) return false;

      const posted =
        this.root.querySelector('[data-bznp-posted]') !== null ||
        window.location.search.indexOf('customer_posted=true') !== -1;

      if (posted) {
        writeState({ subscribed: true });
        this.open();
        this.showSuccess();
      } else {
        this.open();
        this.fail();
      }
      return true;
    }

    clearTriggers() {
      this.timers.forEach(window.clearTimeout);
      this.timers = [];
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onMouseOut) document.removeEventListener('mouseout', this.onMouseOut);
    }

    /* ---------- open / close ---------- */

    open() {
      if (this.isOpen) return;
      this.clearTriggers();
      this.isOpen = true;
      if (!this.designMode) {
        markSeenThisSession();
        writeState({ shownCount: (readState().shownCount || 0) + 1 });
      }
      this.lastFocus = document.activeElement;
      this.root.hidden = false;
      // Next frame, otherwise the transition won't run after hidden
      window.requestAnimationFrame(() => this.root.classList.add('is-open'));
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);

      const target = this.root.querySelector('.bznp__input') || this.root.querySelector('.bznp__close');
      if (target) window.setTimeout(() => target.focus({ preventScroll: true }), 60);
    }

    close(remember) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.root.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
      if (remember !== false && !this.designMode) writeState({ dismissedAt: Date.now() });
      window.setTimeout(() => {
        if (!this.isOpen) this.root.hidden = true;
      }, 300);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------- submit ---------- */

    bind() {
      this.root.querySelectorAll('[data-bznp-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    onSubmit() {
      // Nothing is intercepted — Shopify's captcha and the normal submit take over.
      setPending();
      this.form.classList.add('is-busy');
      if (this.error) this.error.hidden = true;
    }

    showSuccess() {
      if (this.stateInitial) this.stateInitial.hidden = true;
      if (this.stateSuccess) {
        this.stateSuccess.hidden = false;
        // Move focus to the new state, otherwise it stays on the hidden button
        const heading = this.stateSuccess.querySelector('.bznp__heading');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
      }
    }

    fail() {
      if (!this.error) return;
      this.error.hidden = false;
    }
  }

  function init() {
    document.querySelectorAll('.bznp:not([data-bznp-ready])').forEach((root) => {
      root.dataset.bznpReady = 'true';
      new NewsletterPopup(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Section re-render in the theme editor
  document.addEventListener('shopify:section:load', init);
})();
